"use strict";
/**
 * 权限点。
 *
 * 在此之前系统里只有一把尺子：`@MinRole('admin')`。它把三件毫不相干的事
 * 绑在一起 —— 改文案、绑域名、拉同事进来看报表，都要 admin。结果是
 * **客户只能给同事发 owner**，因为编辑发不了文章、运营绑不了域名，
 * 最后所有人都成了管理员，等于没有权限控制。
 *
 * 这一版只做「固定四角色 → 权限点集合」的映射，不做自定义角色：
 * 自定义角色听起来更灵活，实际是让客户自己配出一套"能改文案但不能保存"
 * 的组合，然后半夜打电话。等真有客户提出第五种角色，再把这个映射换成
 * 一张表，调用点一行都不用改 —— 判定永远只经过 `hasPermission`。
 *
 * 三条纪律：
 *
 *   1) **前端隐藏按钮不是权限**。控制台用 `permissions` 决定显示什么，
 *      服务端用 `@RequirePermission` 决定放不放行。两者读同一份映射，
 *      但只有后者算数。
 *   2) **判定只有一个入口**（`hasPermission`）。散落各处的
 *      `role === 'owner' || role === 'admin'` 迟早会漏掉一处。
 *   3) **权限点按"能造成什么后果"切，不按页面切**。`content:publish`
 *      之所以独立，是因为发布会让草稿出现在公网 —— 这是不可撤回的
 *      （站点已被搜索引擎抓到）；而 `content:write` 可以撤回。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROLE_LABELS = exports.ROLE_PERMISSIONS = exports.ROLES = exports.PERMISSIONS = void 0;
exports.isRole = isRole;
exports.isPermission = isPermission;
exports.permissionsFor = permissionsFor;
exports.hasPermission = hasPermission;
exports.hasAllPermissions = hasAllPermissions;
exports.canAssignRole = canAssignRole;
/** 权限点清单。加新点时只加在这里，并同步 `ROLE_PERMISSIONS` */
exports.PERMISSIONS = [
    /** 增删改页面、区块、商品、分类（都还能改回来） */
    'content:write',
    /** 发布 / 取消发布：草稿会出现在公网，被搜索引擎抓走就收不回 */
    'content:publish',
    /** 上传、删除媒体 */
    'media:write',
    /** 站点名称、默认语言、SEO 默认值 */
    'site:update',
    /**
     * 新建站点（企业版多站点）。
     *
     * 与 `site:update` 分开：改站点名可以改回来，建站会**吃掉一个套餐名额**
     * 并且立刻产出一个能被公网访问的新地址。名额是花钱买的，所以这不该和
     * "顺手改个名字"共用一个权限点 —— 否则想收回时就只能把改名一起收回。
     *
     * 恢复被暂停的站点仍归 `site:update`：那只是把已有的东西重新打开，
     * 不新增任何东西。
     */
    'site:create',
    /** 绑定与解绑自有域名 */
    'domain:manage',
    /** 邀请成员、改角色、移除成员 */
    'member:manage',
    /** 下单、升降档 —— 花的是公司的钱，只有 owner */
    'billing:manage',
    /**
     * 查看操作记录。
     *
     * 与 `member:manage` 分开：看得见"谁改了什么"和能不能改别人的角色是两件事。
     * 给 admin 也开着，是因为客户方的负责人被问起"这页谁下线的"时，
     * 让他来找我们要日志，等于审计只服务于我们。
     *
     * 反过来，editor 与 viewer 看不到：日志里有全部成员的操作与邮箱，
     * 那不是内容运营需要的信息。
     */
    'audit:read',
    /**
     * 查看询盘（站上收到的买家留言与资料索取）。
     *
     * 与 `content:write` 分开，因为它读的是**别人的个人信息**：姓名、邮箱、
     * 公司、国家，还有 IP。能改文案不该自动等于能看线索。
     *
     * 刻意**不给 viewer**：viewer 的定位是"外部顾问 / 只看内容"，
     * 把买家的联系方式开放给一个没有雇佣关系的人，属于没必要的信息扩散。
     * 需要看线索就升到 editor —— 传文件、改内容的人本来就天天在接触站点。
     */
    'lead:read',
    /**
     * 发起 AI 翻译。
     *
     * 与 `content:write` 分开的理由与 `content:publish` 同源：**花掉的钱撤不回**。
     * 一次「全部重翻」可能消耗掉整月额度，而写错一个字是可以改回来的。
     * 所以 editor 能校对（写回译文算 `content:write`），但不能开任务烧额度。
     */
    'translation:run',
];
exports.ROLES = ['viewer', 'editor', 'admin', 'owner'];
/**
 * 角色 → 权限点。
 *
 * 刻意写成"逐条列举"而不是"低角色继承高角色"的减法：减法看着短，
 * 但半年后没人说得清 `admin` 到底有哪些权限 —— 只能顺着继承链往上翻。
 *
 * 分工的依据是"这个人在公司里是谁"：
 *   - viewer  客户方的老板 / 外部顾问：只看
 *   - editor  内容运营：写字、传图，但不能发布（发布要过一道人）
 *   - admin   客户方的负责人：能发布、能绑域名，但动不了成员与账单
 *   - owner   签约主体：管钱、管人
 */
exports.ROLE_PERMISSIONS = {
    viewer: [],
    editor: ['content:write', 'media:write', 'lead:read'],
    admin: [
        'content:write',
        'media:write',
        'content:publish',
        'translation:run',
        'site:update',
        'site:create',
        'domain:manage',
        'audit:read',
        'lead:read',
    ],
    owner: [
        'content:write',
        'media:write',
        'content:publish',
        'translation:run',
        'site:update',
        'site:create',
        'domain:manage',
        'member:manage',
        'billing:manage',
        'audit:read',
        'lead:read',
    ],
};
/** 角色显示名。控制台用它渲染下拉框，服务端用它写审计日志 */
exports.ROLE_LABELS = {
    viewer: '只读',
    editor: '内容编辑',
    admin: '管理员',
    owner: '所有者',
};
function isRole(value) {
    return typeof value === 'string' && exports.ROLES.includes(value);
}
function isPermission(value) {
    return typeof value === 'string' && exports.PERMISSIONS.includes(value);
}
function permissionsFor(role) {
    return exports.ROLE_PERMISSIONS[role];
}
/** 唯一的判定入口。服务端 Guard 与前端按钮都走它 */
function hasPermission(role, permission) {
    return exports.ROLE_PERMISSIONS[role].includes(permission);
}
/** 一次判多个权限点，语义是「全部满足」（AND），不是「满足其一」 */
function hasAllPermissions(role, permissions) {
    return permissions.every((p) => hasPermission(role, p));
}
/**
 * 谁能把某个角色改成某个角色。
 *
 * 两条规矩：
 *   - **先要 `member:manage`**。没有这一点的人连成员列表都改不了，
 *     不该因为"他不是 owner"就给出一个模棱两可的答案。
 *   - **只有 owner 能授予 owner**。让 admin 能把别人提成 owner，等于
 *     自己给自己发钱袋子：先提一个人，再让他把自己提上去。
 *
 * 另有一条不在这个函数里：**不能改自己的角色**（控制器里判，因为它要
 * 知道 actor 的 userId 与 target 的 userId，而这里只有角色）。
 *
 * 放在契约层而不是控制器里，是因为控制台也要用同一套规则决定哪些按钮
 * 该灰掉 —— 两边各写一遍必然对不上。
 */
function canAssignRole(actor, target) {
    if (!hasPermission(actor, 'member:manage'))
        return false;
    return actor === 'owner' || target !== 'owner';
}
//# sourceMappingURL=permissions.js.map